import React, { useState } from "react";
import { Card, AntInput, Button, message, Modal, Popconfirm } from "@wellth/web-ui";
import {
  useMemberSecurityQuestionsQuery,
  useVerifySecurityQuestionAnswerMutation,
  useRequestMfaPinResetLazyQuery,
} from "hooks/graphql";
import CARD_STYLES from "../styles";
import {
  BODY_STYLE,
  SECURITY_QUESTION_STYLE,
  INPUT_CONTAINER_STYLE,
  INPUT_STYLE,
  RESET_BUTTON_STYLE,
  RESET_SECTION_STYLE,
} from "./styles";

export interface PinResetProps {
  memberId: string;
}

const PinReset: React.FC<PinResetProps> = ({ memberId }) => {
  const [answer, setAnswer] = useState("");
  const [showResetSuccessModal, setShowResetSuccessModal] = useState(false);
  const [showVerificationResultModal, setShowVerificationResultModal] = useState(false);
  const [verificationResult, setVerificationResult] = useState<{ isCorrect: boolean; message: string } | null>(null);

  const {
    data: securityQuestionData,
    loading: questionLoading,
    error: questionError,
  } = useMemberSecurityQuestionsQuery({
    variables: { memberId },
    onError: (error) => {
      message.error(`Error fetching security question: ${error.message}`);
    },
  });

  const securityQuestions = securityQuestionData?.memberSecurityQuestions || [];
  const firstQuestion = securityQuestions.length > 0 ? securityQuestions[0] : null;

  const [verifySecurityQuestionAnswer, { loading: verifyLoading }] =
    useVerifySecurityQuestionAnswerMutation({
      onCompleted: (data) => {
        const isCorrect = data?.verifySecurityQuestionAnswer?.success;
        const message = isCorrect ? "Correct Security Answer" : "Incorrect Security Answer";

        setVerificationResult({ isCorrect, message });
        setShowVerificationResultModal(true);

        if (isCorrect) {
          setAnswer("");
        }
      },
      onError: (error) => {
        message.error(`Error verifying answer: ${error.message}`);
      },
    });

  const [requestMfaPinReset, { loading: resetLoading }] =
    useRequestMfaPinResetLazyQuery({
      onCompleted: (data) => {
        const success = data?.requestMfaPinReset?.success;
        if (success) {
          setShowResetSuccessModal(true);
        } else {
          message.error("PIN reset failed");
        }
      },
      onError: (error) => {
        message.error(`Error resetting PIN: ${error.message}`);
      },
    });

  const confirmSendAnswer = async () => {
    if (!answer.trim()) {
      message.error("Please enter an answer");
      return;
    }
    await verifySecurityQuestionAnswer({
      variables: {
        input: {
          memberId,
          questionId: firstQuestion.id,
          answer: answer.trim(),
        }
      },
    });
  };

  const confirmResetPin = () => {
    requestMfaPinReset({
      variables: { memberId },
    });
  };

  const handleAnswerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value.length <= 40) {
      setAnswer(value);
    }
  };

  if (questionLoading) {
    return (
      <Card
        headStyle={CARD_STYLES.head}
        bodyStyle={BODY_STYLE}
        style={CARD_STYLES.card}
        title="PIN Reset"
      >
        <div>Loading security question...</div>
      </Card>
    );
  }

  if (questionError) {
    return (
      <Card
        headStyle={CARD_STYLES.head}
        bodyStyle={BODY_STYLE}
        style={CARD_STYLES.card}
        title="PIN Reset"
      >
        <div>Error loading security question</div>
      </Card>
    );
  }



  return (
    <>
      <Card
        headStyle={CARD_STYLES.head}
        bodyStyle={BODY_STYLE}
        style={CARD_STYLES.card}
        title="PIN Reset"
      >
        {firstQuestion ? (
          <>
            <div style={SECURITY_QUESTION_STYLE}>Security question</div>
            <div style={{ marginBottom: "1rem", fontSize: "1.125rem", fontWeight: "bold" }}>
              {firstQuestion.question.en}
            </div>
            <div style={INPUT_CONTAINER_STYLE}>
              <AntInput
                style={INPUT_STYLE}
                placeholder="Enter answer..."
                value={answer}
                onChange={handleAnswerChange}
                maxLength={40}
              />
              <Popconfirm
                title="Are you sure you want to submit this answer?"
                onConfirm={confirmSendAnswer}
                okText="Yes"
                cancelText="Cancel"
                disabled={!answer.trim()}
              >
                <Button
                  type="link"
                  loading={verifyLoading}
                  disabled={!answer.trim()}
                  style={{ padding: 0, height: "auto", color: "black" }}
                >
                  Send
                </Button>
              </Popconfirm>
            </div>
            <div style={RESET_SECTION_STYLE}>
              <Popconfirm
                title="Are you sure you want to reset the PIN?"
                onConfirm={confirmResetPin}
                okText="Yes"
                cancelText="Cancel"
              >
                <Button
                  type="primary"
                  style={RESET_BUTTON_STYLE}
                  loading={resetLoading}
                >
                  Reset PIN
                </Button>
              </Popconfirm>
            </div>
          </>
        ) : (
          <div>No security question available</div>
        )}
      </Card>

      <Modal
        title="PIN Reset"
        visible={showResetSuccessModal}
        onOk={() => setShowResetSuccessModal(false)}
        onCancel={() => setShowResetSuccessModal(false)}
        okText="OK"
        cancelButtonProps={{ style: { display: 'none' } }}
      >
        PIN reset initiated
      </Modal>

      <Modal
        title="Security Question"
        visible={showVerificationResultModal}
        onOk={() => setShowVerificationResultModal(false)}
        onCancel={() => setShowVerificationResultModal(false)}
        okText="OK"
        cancelButtonProps={{ style: { display: "none" } }}
      >
        {verificationResult?.message}
      </Modal>
    </>
  );
};

export default PinReset;